import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { deleteJSON, getHost, getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import type { GetApiKeyType, CreatedApiKeyResponseSuccessType, RevokeApiKeyResponseType, CreatedApiKeyBodyType, GetApiKeysResponseSuccessType } from "@whatslly/types/integrations/api-keys/ApiKey";
import { CreateApiKeyModal } from "./modals/CreateApiKeyModal";
import { IApiKeysStore } from "./IApiKeysStore";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { TFunction } from "i18next";
import { parseOrReturnError } from "../../../services/utils";
import { fromPromise } from "mobx-utils";

@injectable()
export class ApiKeysStore implements IApiKeysStore {
  refreshCounter = 0;
  _keys: GetApiKeyType[] = [];
  _keyPair: CreatedApiKeyBodyType | undefined;
  keysUpdating = false;
  connected = false;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      keysUpdating: observable,
      refreshCounter: observable,
      _keys: observable.shallow,
      keys: computed,
      loading: computed,
      _keyPair: observable,
      keyPair: computed,
      loadApiKeys: action.bound,
      connected: observable,
      refresh: action,
      baseUrl: computed,
    });

    reaction(
      () => this.orgStore.orgId.value,
      () => this.refresh()
    );
  }

  private async getBaseUrl() {
    const apiSuffix = `/api/`;
    const host = getHost(`${apiSuffix}/device`, await this.orgStore.wPod);
    return `${host}${apiSuffix}`;
  }

  get baseUrl() {
    return fromPromise(this.keys.then(() => this.getBaseUrl()));
  }

  get loading() {
    return this.keys.state === "pending";
  }

  async openCreateApiKeyModal() {
    this._keyPair = undefined;
    const orgId = await this.getOrgId();
    const authorized = hasPermission(this.userStore.user, UserPermissions["api_keys.update"]);
    if (!orgId || !authorized) {
      return;
    }
    await modalService.openModal(CreateApiKeyModal).promise;
  }

  get keyPair(): CreatedApiKeyBodyType | undefined {
    return this._keyPair;
  }

  set keyPair(val: CreatedApiKeyBodyType) {
    this._keyPair = val;
  }

  async createApiKey({ apiKeyName, t }: { apiKeyName: string; t: TFunction }) {
    const orgId = await this.getOrgId();
    const response = await postJSON({
      urlSuffix: `/integrations/api-keys`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ apiKeyName, orgId }),
    })
      .then((res) => res.json() as Promise<CreatedApiKeyResponseSuccessType>)
      .catch(() => ({ success: false }) as CreatedApiKeyResponseSuccessType);

    if (response.success) {
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
      return response.keys;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
  }

  async revokeApiKey({ apiKey, t }: { apiKey: string; t: TFunction }) {
    const orgId = await this.getOrgId();
    const authorized = hasPermission(this.userStore.user, UserPermissions["api_keys.update"]);
    if (!orgId || !authorized) {
      return;
    }
    const response = await deleteJSON({
      urlSuffix: `/integrations/api-keys`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ apiKey, orgId }),
    })
      .then((res) => res.json() as Promise<RevokeApiKeyResponseType>)
      .catch(() => ({ success: false }) as RevokeApiKeyResponseType);

    if (response.success) {
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
      this.refreshCounter++;
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
  }

  async getOrgId() {
    const org = await this.orgStore.selectedOrg;
    return org?.value ?? this.userStore.user?.organizationId;
  }

  get keys() {
    this.refreshCounter;
    return fromPromise(this.loadApiKeys());
  }

  refresh() {
    this.refreshCounter++;
  }

  async loadApiKeys() {
    const orgId = await this.getOrgId();
    const authorized = hasPermission(this.userStore.user, UserPermissions["api_keys.view"]);
    if (!orgId || !authorized) {
      return [];
    }
    runInAction(() => {
      this.keysUpdating = true;
    });
    return getJSON({
      urlSuffix: `/integrations/api-keys/?orgId=${orgId}`,
      etoken: this.userStore.etoken ?? "",
    })
      .then((res) => parseOrReturnError<GetApiKeysResponseSuccessType>(res, this.logger))
      .then((res) => {
        runInAction(() => {
          this.keysUpdating = false;
        });
        return res.success ? res?.keys : [];
      });
  }
}
